import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  Workflow, 
  Settings,
  Save,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  TrendingUp,
  UserPlus,
  FileCheck
} from 'lucide-react';
import apiService from '../services/api';

const SlackWorkflowManager = () => {
  const [workflowRules, setWorkflowRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const eventTypes = [
    {
      id: 'deal_signed',
      name: 'Deal Agreement Signed',
      description: 'When a deal moves to "Agreement Signed" status',
      icon: FileCheck,
      color: 'text-green-600'
    },
    {
      id: 'new_lead',
      name: 'New Lead Created',
      description: 'When a new deal/lead is submitted',
      icon: UserPlus,
      color: 'text-blue-600'
    },
    {
      id: 'deal_stage_change',
      name: 'Deal Stage Change',
      description: 'When a deal moves between pipeline stages',
      icon: TrendingUp,
      color: 'text-orange-600'
    }
  ];

  useEffect(() => {
    loadWorkflowRules();
  }, []);

  const loadWorkflowRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.request('/slack/workflows');
      setWorkflowRules(result.workflow_rules || []);
    } catch (error) {
      console.error('Error loading workflow rules:', error);
      setError('Failed to load workflow rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRules = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      await apiService.request('/slack/workflows', {
        method: 'PUT',
        body: JSON.stringify(workflowRules)
      });
      
      setSuccessMessage('Workflow rules saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error saving workflow rules:', error);
      setError('Failed to save workflow rules: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateRule = (ruleId, updates) => {
    setWorkflowRules(rules =>
      rules.map(rule =>
        rule.id === ruleId ? { ...rule, ...updates } : rule
      )
    );
  };

  const toggleRule = (ruleId) => {
    const rule = workflowRules.find(r => r.id === ruleId);
    updateRule(ruleId, { enabled: !rule.enabled });
  };

  const getDefaultMessageTemplate = (eventType) => {
    switch (eventType) {
      case 'deal_signed':
        return '🎉 *Agreement Signed!*\n\n*Company:* {company_name}\n*Deal:* {deal_name}\n*Value:* ${offered_price}/month\n\nGreat work team! 💪';
      case 'new_lead':
        return '🔥 *New Lead Alert!*\n\n*Company:* {company_name}\n*Deal:* {deal_name}\n*Location:* {location_name}\n\nTime to follow up! 📞';
      case 'deal_stage_change':
        return '📈 *Deal Update*\n\n*Company:* {company_name}\n*Deal:* {deal_name}\n*Stage Change:* {old_stage} → {new_stage}\n\nKeep the momentum going! 🚀';
      default:
        return '';
    }
  };

  const addCustomRule = () => {
    const newRule = {
      id: `rule_${Date.now()}`,
      event_type: 'deal_stage_change',
      enabled: true,
      channel: '',
      message_template: getDefaultMessageTemplate('deal_stage_change'),
      conditions: {}
    };
    setWorkflowRules([...workflowRules, newRule]);
  };

  const removeRule = (ruleId) => {
    setWorkflowRules(rules => rules.filter(rule => rule.id !== ruleId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading workflow rules...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Workflow className="w-6 h-6 text-slate-600" />
          <div>
            <h3 className="font-medium">Slack Notification Workflows</h3>
            <p className="text-sm text-gray-600">
              Configure which events trigger Slack notifications and customize messages
            </p>
          </div>
        </div>
        <Button onClick={addCustomRule} variant="outline" size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Add Rule
        </Button>
      </div>

      {/* Workflow Rules */}
      <div className="space-y-4">
        {workflowRules.map((rule) => {
          const eventType = eventTypes.find(et => et.id === rule.event_type);
          const IconComponent = eventType?.icon || MessageSquare;
          
          return (
            <Card key={rule.id}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <IconComponent className={`w-5 h-5 ${eventType?.color || 'text-gray-600'}`} />
                    <div>
                      <CardTitle className="text-lg">
                        {eventType?.name || rule.event_type}
                      </CardTitle>
                      <p className="text-sm text-gray-600">
                        {eventType?.description || 'Custom workflow rule'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => toggleRule(rule.id)}
                      variant="ghost"
                      size="sm"
                      className="p-1"
                    >
                      {rule.enabled ? (
                        <ToggleRight className="w-8 h-8 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-gray-400" />
                      )}
                    </Button>
                    <Badge className={rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Button
                      onClick={() => removeRule(rule.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Event Type Selection */}
                <div>
                  <Label htmlFor={`event_type_${rule.id}`}>Event Type</Label>
                  <select
                    id={`event_type_${rule.id}`}
                    className="w-full p-2 border border-gray-300 rounded-md mt-1"
                    value={rule.event_type}
                    onChange={(e) => {
                      const newEventType = e.target.value;
                      updateRule(rule.id, {
                        event_type: newEventType,
                        message_template: rule.message_template || getDefaultMessageTemplate(newEventType)
                      });
                    }}
                  >
                    {eventTypes.map((et) => (
                      <option key={et.id} value={et.id}>
                        {et.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom Channel */}
                <div>
                  <Label htmlFor={`channel_${rule.id}`}>
                    Custom Channel (optional)
                  </Label>
                  <Input
                    id={`channel_${rule.id}`}
                    placeholder="#deals or @username (leave empty for default)"
                    value={rule.channel || ''}
                    onChange={(e) => updateRule(rule.id, { channel: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Override the default channel for this notification
                  </p>
                </div>

                {/* Message Template */}
                <div>
                  <Label htmlFor={`message_${rule.id}`}>
                    Message Template
                  </Label>
                  <Textarea
                    id={`message_${rule.id}`}
                    placeholder="Enter custom message template..."
                    value={rule.message_template || ''}
                    onChange={(e) => updateRule(rule.id, { message_template: e.target.value })}
                    rows={4}
                    className="mt-1 font-mono text-sm"
                  />
                  <div className="text-xs text-gray-500 mt-1 space-y-1">
                    <p>Available variables: {'{company_name}'}, {'{deal_name}'}, {'{offered_price}'}, {'{location_name}'}</p>
                    <p>For stage changes: {'{old_stage}'}, {'{new_stage}'}</p>
                  </div>
                  
                  {/* Reset to Default */}
                  <Button
                    onClick={() => updateRule(rule.id, { 
                      message_template: getDefaultMessageTemplate(rule.event_type) 
                    })}
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                  >
                    Reset to Default Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {workflowRules.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <Workflow className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 mb-2">No workflow rules configured</p>
            <p className="text-sm text-gray-400">Add rules to configure when Slack notifications are sent</p>
            <Button onClick={addCustomRule} className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Rule
            </Button>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button 
          onClick={handleSaveRules} 
          disabled={saving}
          className="flex-1 max-w-xs"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Workflow Rules
            </>
          )}
        </Button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-700">{successMessage}</span>
        </div>
      )}
    </div>
  );
};

export default SlackWorkflowManager;